import { AlertTriangle, Info, LogOut } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useAuthStore } from '@/store/auth';
import { decodeJwtSub } from '@/lib/jwt';
import { logoutWithReason } from '@/lib/auth-events';
import styles from './NonDriverBanner.module.css';

/**
 * Two-mode banner shown at the top of driver-tier surfaces
 * (History, Profile):
 *
 *   1. Desync warning (hard variant)
 *      The active access token's `sub` claim doesn't match the
 *      profile user's `_id`. This is a CORRUPTED auth store:
 *      every API request will scope to the token's user but the
 *      UI renders for the profile user. Renders an explicit
 *      sign-out CTA — recoverable in one tap.
 *
 *   2. Non-driver advisory (soft variant)
 *      The auth store is internally consistent but the user's
 *      role is something other than 'driver' (super_admin,
 *      admin, building_admin, billing_admin). The PWA widgets
 *      aggregate the caller's PERSONAL data, which for a non-
 *      driver is typically zero. The advisory explains the
 *      empty widgets without alarming the operator.
 *
 * Production drivers never see either banner — they're role
 * 'driver' AND their token sub always matches their _id. Both
 * variants exist to head off dev / ops confusion that already
 * cost real diagnostic time multiple times in a single session
 * (sessions / activity widgets reading zero because the active
 * session was stamped to a different account).
 *
 * Persistent — no dismiss control. Operators muscle-memory
 * dismissibles closed; both banners are short enough to live
 * with, and the hard variant is supposed to demand attention.
 */
export function NonDriverBanner() {
  const { t } = useTranslation('chrome');
  const user = useAuthStore((s) => s.user);
  const accessToken = useAuthStore((s) => s.accessToken);

  // ── Desync detection ───────────────────────────────────────
  // Decode the JWT's payload segment to read its `sub` claim.
  // Compare against the profile's _id. Divergence is the
  // hard-warning case.
  //
  // Defensive shape: decodeJwtSub returns null when the token
  // is malformed / unparseable / not a JWT — we treat that as
  // "no useful token info" rather than "desync confirmed", so
  // a malformed token doesn't false-trigger the warning.
  const tokenSub = accessToken ? decodeJwtSub(accessToken) : null;
  const userId = user?._id ?? null;
  const desync = Boolean(tokenSub && userId && tokenSub !== userId);

  if (desync) {
    return <DesyncWarning userId={userId!} tokenSub={tokenSub!} />;
  }

  // ── Non-driver advisory ────────────────────────────────────
  // Render nothing for the production case (driver) and the
  // signed-out case (no user). Auth-gated routes already
  // redirect unauthenticated users so role === undefined here
  // is rare but defensive.
  const role = user?.role;
  if (!role || role === 'driver') return null;

  return (
    <div className={styles.banner} role="status">
      <Info size={16} className={styles.icon} aria-hidden />
      <p className={styles.copy}>
        {t('banner.signedInAs')}{' '}
        <span className={styles.rolePill}>{role}</span>.
        {' '}{t('banner.advisoryBody')}
      </p>
    </div>
  );
}

/**
 * Hard-warning variant. Distinct visual chrome (warn token
 * background, AlertTriangle icon) so the operator immediately
 * sees this is more severe than the soft advisory. The Sign
 * Out button clears the auth store entirely (token + user) and
 * redirects to /login — same recovery path the API client's
 * 401 handler uses when refresh fails. Hard refresh isn't
 * required; clearAuth flips isAuthenticated to false which the
 * App component re-renders into the LoginPage branch.
 */
function DesyncWarning({
  userId, tokenSub,
}: {
  userId: string;
  tokenSub: string;
}) {
  const { t } = useTranslation('chrome');
  const handleSignOut = (): void => {
    logoutWithReason('manual-logout', {
      trigger: 'desync-banner',
      userId,
      tokenSub,
    });
    // Same redirect contract as the api.ts 401-handler: only
    // navigate when we're not already on /login (the auth-store
    // clear will trigger App's re-render into the LoginPage
    // branch, but defensive direct-nav handles edge cases where
    // the store update doesn't trigger a re-render in time).
    if (!window.location.pathname.startsWith('/login')) {
      window.location.href = '/login';
    }
  };

  return (
    <div className={`${styles.banner} ${styles.bannerWarn}`} role="alert">
      <AlertTriangle size={16} className={styles.iconWarn} aria-hidden />
      <div className={styles.warnBody}>
        <p className={styles.copy}>
          <strong>{t('banner.desyncTitle')}</strong>
          {' '}{t('banner.desyncFix')}
        </p>
        {/* Diagnostic detail — hidden in normal flow but shown
            on hover/focus for ops debugging. The user values
            don't leak any secret material; both are public
            ObjectId hex strings. */}
        <p className={styles.warnDetail}>
          token sub: <code>{tokenSub}</code> · profile _id: <code>{userId}</code>
        </p>
      </div>
      <button
        type="button"
        className={styles.signOutBtn}
        onClick={handleSignOut}
        aria-label={t('banner.signOut')}
      >
        <LogOut size={14} aria-hidden />
        {t('banner.signOut')}
      </button>
    </div>
  );
}

// decodeJwtSub lifted to apps/web/src/lib/jwt.ts so the
// api.ts refresh hook can share the same decode logic. See
// import at the top of the file.
